// src/components/issues/visuelEvenement.tsx — SCE Issues
// Icône et couleurs d'un événement d'activité, par type.
//
// Partagé entre l'activité de la fiche problème et celle de l'accueil (Lot 9) :
// un même événement doit se reconnaître au même rond, d'un écran à l'autre.

import {
  ArrowRightCircle,
  CheckCircle2,
  MessageSquare,
  Paperclip,
  PenLine,
  PenTool,
  Plus,
  Send,
  Tag,
  XCircle,
} from 'lucide-react'
import type { CommentaireType } from '@/types/issue.types'
import { lireEvenementEnvoi } from '@/utils/envoiBE'
import { natureAutomatique } from '@/utils/rattachementPhotos'

export interface VisuelEvenement {
  icon: JSX.Element
  bg: string
  color: string
}

/**
 * `contenu` : le texte de l'événement, pour reconnaître un envoi au BE (une
 * « Modification » à l'écriture) et lui donner l'icône du bouton Envoyer.
 */
export function visuelEvenement(type: CommentaireType, taille = 16, contenu?: string): VisuelEvenement {
  if (type === 'Modification' && contenu && lireEvenementEnvoi(contenu)) {
    return { icon: <Send size={taille} />, bg: 'var(--color-gray-80)', color: 'var(--color-white)' }
  }
  switch (type) {
    case 'Creation':
      // Le « + » du bouton Déclarer, noir sur jaune comme lui (26/10, Guillaume) :
      // le bonhomme d'avant redisait la photo de l'auteur, et son vert se
      // confondait avec la clôture (vert = Clos).
      return {
        icon: <Plus size={taille} strokeWidth={2.6} />,
        bg: 'var(--color-yellow)',
        color: 'var(--color-black)',
      }
    case 'Commentaire': {
      // Des fichiers envoyés sans message (27/10) : le trombone ; une photo ou un
      // PDF annoté : la plume. Même rond gris : c'est toujours un commentaire.
      const auto = contenu ? natureAutomatique(contenu) : null
      return {
        icon: auto?.genre === 'fichiers' ? <Paperclip size={taille} /> : auto?.genre === 'annotation' ? <PenTool size={taille} /> : <MessageSquare size={taille} />,
        bg: 'var(--color-gray-20)',
        color: 'var(--color-text-primary)',
      }
    }
    case 'ChangementStatut':
      return {
        icon: <ArrowRightCircle size={taille} />,
        bg: 'var(--color-teal)',
        // Noir : le blanc sur teal tombe à 2,4:1 (même règle que le texte des
        // badges métier, Lot 8).
        color: 'var(--color-black)',
      }
    case 'Cloture':
      // Vert et non rouge : le design system réserve le rouge Traffic Light à
      // « Critique / Erreur » et le vert à « Clos / Succès ». Une clôture
      // affichée en rouge se lisait comme un incident — à contresens du badge
      // Clos, vert, affiché en tête de la même fiche.
      return {
        icon: <CheckCircle2 size={taille} />,
        bg: 'var(--color-traffic-green)',
        color: 'var(--color-white)',
      }
    case 'Qualification':
      return {
        icon: <Tag size={taille} />,
        bg: 'var(--color-yellow)',
        color: 'var(--color-black)',
      }
    case 'Annulation':
      // Gris : un annulé n'est ni un succès ni une erreur, c'est un écart.
      return {
        icon: <XCircle size={taille} />,
        bg: 'var(--color-gray-70)',
        color: 'var(--color-white)',
      }
    case 'Modification':
      // Référence qualité, N° de plan (Lot 3 bis), édition (Lot 6) : une
      // correction de donnée, pas une étape du cycle. Gris foncé : un fond
      // blanc aurait disparu sur le plan gris 5 de la page.
      return {
        icon: <PenLine size={taille} />,
        bg: 'var(--color-gray-80)',
        color: 'var(--color-white)',
      }
    default: {
      // Garde d'exhaustivité : un type ajouté sans visuel ne compile pas.
      const inconnu: never = type
      void inconnu
      return { icon: <MessageSquare size={taille} />, bg: 'var(--color-gray-20)', color: 'var(--color-text-primary)' }
    }
  }
}
